import type { FactoryForm } from '@indutrust/factory-model';

/**
 * Stable identifiers for every section card rendered by `FactoryEdit`.
 *
 * Used by the side-nav, the per-section anchors, and the completeness
 * util — keep them in sync with the order they are rendered in
 * `factory-edit.ts`.
 */
export type FactorySection =
  | 'general'
  | 'location'
  | 'machinePark'
  | 'capabilities'
  | 'business'
  | 'projectReferences'
  | 'documents'
  | 'gallery'
  | 'virtualTour'
  | 'contacts';

/**
 * Per-section completeness signal.
 *
 * - `invalid` — at least one **hard-required** field (per
 *   `libs/factory/SPEC.md` Q5) is missing / invalid. Drives a red dot
 *   in the side nav and blocks "Submit for approval".
 * - `incomplete` — passes the hard requirements but at least one
 *   **soft-recommended** field (per Q5 soft list) is empty. Drives an
 *   amber dot in the side nav.
 * - `complete` — both hard and soft requirements satisfied. Green dot.
 */
export type FactorySectionValidity = 'complete' | 'incomplete' | 'invalid';

/** Side-nav row rendered by the factory editor section nav. */
export type FactorySectionNavItem = {
  icon: string;
  id: FactorySection;
  validity: FactorySectionValidity;
};

/** Side-collection counts needed to fully evaluate a few sections. */
export type FactorySectionDeps = {
  /** Number of project references already attached to the factory. */
  projectReferencesCount: number;
  /** Number of documents already uploaded for the factory. */
  documentsCount: number;
  /** Number of gallery images uploaded for the factory. */
  imagesCount: number;
  /** Number of machine park entries attached to the factory. */
  machineParkCount: number;
  /** Number of virtual-tour panorama nodes attached to the factory. */
  virtualTourNodesCount: number;
};

const SECTION_ORDER: readonly FactorySection[] = [
  'general',
  'location',
  'machinePark',
  'capabilities',
  'business',
  'projectReferences',
  'documents',
  'gallery',
  'virtualTour',
  'contacts',
] as const;

/**
 * PrimeIcons class fragments rendered next to each section title and
 * in the side-nav list. Keep these aligned with the icons rendered by
 * the matching section card in `factory-edit.ts`.
 */
export const FACTORY_SECTION_ICONS: Readonly<Record<FactorySection, string>> = {
  general: 'info-circle',
  location: 'map-marker',
  machinePark: 'cog',
  capabilities: 'wrench',
  business: 'briefcase',
  projectReferences: 'folder-open',
  documents: 'file',
  gallery: 'image',
  virtualTour: 'images',
  contacts: 'users',
};

/**
 * PrimeIcons class fragments for section validity indicators.
 * Aligns soft/hard states with the publication stepper icons (draft / pending).
 */
export const FACTORY_SECTION_VALIDITY_ICONS: Readonly<
  Record<FactorySectionValidity, string>
> = {
  complete: 'check',
  incomplete: 'file-edit',
  invalid: 'exclamation-circle',
};

function isFiniteNumber(value: unknown): boolean {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Like {@link isFiniteNumber} but also accepts numeric strings written by
 * `idt-input` (which always stores values as strings via `ngModel`).
 */
function isFilledNumber(value: unknown): boolean {
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  if (typeof value === 'string' && value.trim() !== '') {
    return Number.isFinite(Number(value));
  }
  return false;
}

function isNonEmptyString(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function isPositiveId(value: unknown): boolean {
  return typeof value === 'number' && value > 0;
}

function resolveGeneral(value: FactoryForm): FactorySectionValidity {
  if (!isNonEmptyString(value.name)) {
    return 'invalid';
  }
  const hasWebsite = isNonEmptyString(value.website);
  const hasDescription = isNonEmptyString(value.description);
  return hasWebsite && hasDescription ? 'complete' : 'incomplete';
}

function resolveLocation(value: FactoryForm): FactorySectionValidity {
  const required = [
    isPositiveId(value.country_id),
    isPositiveId(value.industrial_area_id),
    isNonEmptyString(value.address),
    isFiniteNumber(value.latitude),
    isFiniteNumber(value.longitude),
  ];
  return required.every(Boolean) ? 'complete' : 'invalid';
}

function resolveCapabilities(value: FactoryForm): FactorySectionValidity {
  return (value.capability_ids ?? []).length > 0 ? 'complete' : 'invalid';
}

function resolveBusiness(value: FactoryForm): FactorySectionValidity {
  const filled = [
    value.certification.length > 0,
    isNonEmptyString(value.creation_date),
    isNonEmptyString(value.trade_license_number),
    isFilledNumber(value.headcount_production),
    isFilledNumber(value.headcount_management),
    isFilledNumber(value.workshop_used),
  ].filter(Boolean).length;
  return filled >= 3 ? 'complete' : 'incomplete';
}

function resolveContacts(value: FactoryForm): FactorySectionValidity {
  const count = value.contact_ids.length;
  if (count === 0) {
    return 'invalid';
  }
  return count >= 2 ? 'complete' : 'incomplete';
}

/**
 * Returns the full ordered list of section ids — handy for the side
 * nav so the consumer doesn't have to repeat the order locally.
 */
export function listFactorySections(): readonly FactorySection[] {
  return SECTION_ORDER;
}

/**
 * Computes the {@link FactorySectionValidity} for a given section from
 * the current form value plus the side-collection counts.
 *
 * Pure: only reads from `value` and `deps`, never mutates anything.
 * Designed to be called from a `computed()` in the editor so the side
 * nav reacts live as the user types.
 *
 * @example
 * const status = computed(() =>
 *   getFactorySectionStatus('location', factoryModel(), {
 *     projectReferencesCount: this.refs().length,
 *     documentsCount: this.docs().length,
 *   })
 * );
 */
export function getFactorySectionStatus(
  section: FactorySection,
  value: FactoryForm,
  deps: FactorySectionDeps
): FactorySectionValidity {
  switch (section) {
    case 'general':
      return resolveGeneral(value);
    case 'location':
      return resolveLocation(value);
    case 'capabilities':
      return resolveCapabilities(value);
    case 'business':
      return resolveBusiness(value);
    case 'machinePark':
      return deps.machineParkCount > 0 ? 'complete' : 'incomplete';
    case 'projectReferences':
      return deps.projectReferencesCount > 0 ? 'complete' : 'incomplete';
    case 'documents':
      return deps.documentsCount > 0 ? 'complete' : 'incomplete';
    case 'gallery':
      return deps.imagesCount > 0 ? 'complete' : 'incomplete';
    case 'virtualTour':
      return deps.virtualTourNodesCount > 0 ? 'complete' : 'incomplete';
    case 'contacts':
      return resolveContacts(value);
  }
}
